import { LOAD_TIERS } from "@/data/pricing";
import { PRICING } from "@/constants/testIds";
import { WHATSAPP_NUMBER } from "@/data/brand";
import { Check, Package } from "lucide-react";
import { toast } from "sonner";

// Simple inline WhatsApp glyph so we don't need a new dependency.
function WhatsAppIcon({ size = 14 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M20.52 3.48A11.94 11.94 0 0 0 12.06 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.14 1.6 5.94L0 24l6.32-1.66a11.9 11.9 0 0 0 5.74 1.46h.01c6.56 0 11.9-5.34 11.9-11.9 0-3.18-1.24-6.17-3.45-8.42Zm-8.46 18.3a9.9 9.9 0 0 1-5.05-1.38l-.36-.22-3.75.98 1-3.66-.24-.38a9.87 9.87 0 0 1-1.52-5.22c0-5.46 4.44-9.9 9.92-9.9 2.65 0 5.14 1.03 7.02 2.9a9.86 9.86 0 0 1 2.9 7.02c0 5.46-4.44 9.86-10 9.86Zm5.42-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.46-.15-.66.15-.2.3-.76.97-.94 1.17-.17.2-.34.22-.63.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.65-2.05-.17-.3-.02-.46.13-.6.13-.13.3-.34.44-.5.15-.17.2-.28.3-.48.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.18-.24-.58-.48-.5-.66-.5h-.56c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.1 4.48.71.3 1.27.49 1.7.63.72.23 1.37.2 1.88.12.57-.08 1.76-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.08-.13-.27-.2-.57-.34Z" />
    </svg>
  );
}

export default function Pricing() {
  const onWhatsApp = (tier) => {
    const bagsPart = tier.bags ? `${tier.bags}, ` : "";
    const message =
      `Hi Kay! I'd like to book a ${tier.name} (${bagsPart}${tier.weight}) — £${tier.ourPrice}. ` +
      `Please could you confirm availability?`;
    if (!WHATSAPP_NUMBER) {
      toast.message("WhatsApp coming soon", {
        description: "We'll add our WhatsApp number here shortly.",
      });
      return;
    }
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section
      id="pricing"
      data-testid={PRICING.root}
      className="border-b-2 border-black bg-[var(--kay-bg)]"
    >
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-8 md:py-28">
        <div className="mb-10 flex flex-col items-start justify-between gap-6 md:mb-14 md:flex-row md:items-end">
          <div>
            <div className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-[var(--kay-pink)]">
              Book Online
            </div>
            <h2 className="font-display text-4xl uppercase leading-none sm:text-5xl md:text-6xl">
              Simple, honest
              <br />
              <span className="text-[var(--kay-pink)]">pricing.</span>
            </h2>
          </div>
          <div className="max-w-md text-neutral-700 sm:text-lg">
            All prices are estimates based on volume or weight. Actual price is
            confirmed on collection.
          </div>
        </div>

        {/* How it works notice */}
        <div className="mb-10 border-2 border-black bg-black p-5 text-white hard-shadow md:p-6">
          <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--kay-pink)]">
                How it works
              </div>
              <div className="mt-1 font-display text-xl uppercase sm:text-2xl">
                Book on WhatsApp · Pay on collection
              </div>
            </div>
            <div className="text-sm text-neutral-300 md:max-w-sm md:text-right">
              Pick your load size, tell us when suits you and we'll be in touch
              to confirm. Payment is settled with our friendly driver when we
              collect.
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {LOAD_TIERS.map((t) => {
            const savings = t.skipPrice ? t.skipPrice - t.ourPrice : null;
            return (
              <div
                key={t.key}
                data-testid={PRICING.card(t.key)}
                className="group relative flex flex-col border-2 border-black bg-white p-5 hard-shadow hover-lift"
              >
                {savings ? (
                  <div className="absolute -right-2 -top-2 rotate-[6deg] border-2 border-black bg-[var(--kay-pink)] px-2 py-1 text-[11px] font-bold uppercase tracking-widest text-black">
                    Save £{savings}
                  </div>
                ) : (
                  <div className="absolute -right-2 -top-2 rotate-[6deg] border-2 border-black bg-yellow-300 px-2 py-1 text-[11px] font-bold uppercase tracking-widest">
                    Starter
                  </div>
                )}

                <div className="mb-4 inline-flex h-10 w-10 items-center justify-center border-2 border-black bg-black text-[var(--kay-pink)]">
                  <Package size={18} />
                </div>

                <h3 className="font-display text-xl uppercase leading-tight sm:text-2xl">
                  {t.name}
                </h3>
                <p className="mt-1 text-sm text-neutral-600">{t.equivalent}</p>

                <div className="mt-5 flex items-baseline gap-3">
                  <div className="font-display text-4xl leading-none">
                    £{t.ourPrice}
                  </div>
                  {t.skipPrice && (
                    <div className="text-sm text-neutral-500">
                      Skip:{" "}
                      <span className="line-through">£{t.skipPrice}</span>
                    </div>
                  )}
                </div>

                <ul className="mt-5 space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <Check size={16} className="mt-0.5 text-[var(--kay-pink)]" />
                    <span>{t.size}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={16} className="mt-0.5 text-[var(--kay-pink)]" />
                    <span>{t.weight}</span>
                  </li>
                  {t.bags && (
                    <li className="flex items-start gap-2">
                      <Check size={16} className="mt-0.5 text-[var(--kay-pink)]" />
                      <span>{t.bags}</span>
                    </li>
                  )}
                </ul>

                <button
                  data-testid={PRICING.bookBtn(t.key)}
                  onClick={() => onWhatsApp(t)}
                  className="mt-6 inline-flex items-center justify-center gap-2 border-2 border-black bg-[#25D366] px-3 py-2.5 font-display text-[11px] uppercase tracking-widest text-black hard-shadow-sm hover-lift"
                >
                  <WhatsAppIcon size={14} />
                  WhatsApp Me To Book
                </button>
              </div>
            );
          })}
        </div>

        <p className="mt-10 max-w-3xl text-sm text-neutral-500">
          * Skip prices shown are typical local skip-hire averages for
          comparison. Prices are estimates — the exact price is confirmed on
          collection based on final volume/weight.
        </p>
      </div>
    </section>
  );
}
